<template>
  <div class="scroll-text" ref="container">
    <div class="scroll-text__inner" :class="{ 'is-scrollable': scrollable }" :style="innerStyle" ref="inner">
      <span class="scroll-text__content" ref="content">{{ text }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ScrollText',
  props: {
    text: {
      type: [String, Number],
      default: ''
    },
    speed: {
      type: Number,
      default: 0.5
    }
  },
  data() {
    return {
      offset: 0,
      direction: 1,
      containerWidth: 0,
      textWidth: 0,
      scrollable: false,
      timer: null,
      measureTimer: null,
      retryCount: 0
    }
  },
  computed: {
    innerStyle() {
      const style = {
        transform: `translate3d(${-this.offset}px, 0, 0)`
      }
      if (this.scrollable && this.textWidth) {
        style.width = `${this.textWidth}px`
      }
      return style
    },
    maxOffset() {
      if (!this.scrollable) return 0
      return Math.max(0, Math.ceil(this.textWidth - this.containerWidth))
    }
  },
  watch: {
    text() {
      this.reset()
      this.scheduleMeasure()
    }
  },
  mounted() {
    this.scheduleMeasure()
  },
  beforeDestroy() {
    this.stop()
    if (this.measureTimer) clearTimeout(this.measureTimer)
  },
  methods: {
    scheduleMeasure(delay = 80) {
      if (this.measureTimer) clearTimeout(this.measureTimer)
      this.measureTimer = setTimeout(() => {
        this.measureTimer = null
        this.measure()
      }, delay)
    },
    measure() {
      this.$nextTick(() => {
        const container = this.$refs.container
        const content = this.$refs.content
        if (!container || !content) {
          if (this.retryCount < 10) {
            this.retryCount += 1
            this.scheduleMeasure(120)
          }
          return
        }

        const containerWidth = container.getBoundingClientRect().width
        const textWidth = content.getBoundingClientRect().width

        if (!containerWidth || !textWidth) {
          if (this.retryCount < 10) {
            this.retryCount += 1
            this.scheduleMeasure(120)
          }
          return
        }

        this.retryCount = 0
        this.containerWidth = Math.ceil(containerWidth)
        this.textWidth = Math.ceil(textWidth)
        this.scrollable = this.textWidth > this.containerWidth + 1
        this.offset = 0
        this.direction = 1

        if (this.scrollable) {
          this.start()
        } else {
          this.stop()
        }
      })
    },
    start() {
      this.stop()
      this.timer = setInterval(() => {
        this.tick()
      }, 16)
    },
    stop() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    reset() {
      this.stop()
      this.offset = 0
      this.direction = 1
      this.scrollable = false
      this.containerWidth = 0
      this.textWidth = 0
    },
    tick() {
      if (!this.scrollable || this.maxOffset <= 0) {
        this.stop()
        return
      }

      const nextOffset = this.offset + this.direction * this.speed
      if (nextOffset >= this.maxOffset) {
        this.offset = this.maxOffset
        this.direction = -1
        return
      }
      if (nextOffset <= 0) {
        this.offset = 0
        this.direction = 1
        return
      }
      this.offset = nextOffset
    }
  }
}
</script>

<style lang="scss" scoped>
.scroll-text {
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-align: center;
}

.scroll-text__inner {
  display: inline-block;
  min-width: 100%;
  white-space: nowrap;
  text-align: center;
  will-change: transform;

  &.is-scrollable {
    min-width: auto;
    text-align: left;
  }
}

.scroll-text__content {
  display: inline-block;
  white-space: nowrap;
}
</style>
